{
  "$schema": "https://cogo.xyz/ontology/v1/domain-flow-patterns",
  "version": "1.0.0",
  "description": "Domain-specific required flow patterns. Used by Stage 2.5 LLM evaluator and validator to ensure apps have all necessary interaction flows for their domain.",

  "commerce": {
    "description": "E-commerce store patterns — product browsing, cart management, checkout",
    "detectWhen": {
      "pageRoutes": ["/product/:id", "/cart", "/checkout"],
      "blockTypes": ["product-grid", "product-detail", "cart-view", "checkout-form"],
      "keywords": ["store", "shop", "commerce", "product", "cart", "checkout"]
    },
    "requiredFlows": [
      {
        "id": "product-navigation",
        "description": "Product card click → view product detail page",
        "level": "error",
        "block": "product-grid|bestsellers-grid|product-list",
        "trigger": "product-card container or image:tap (iterated element)",
        "steps": [
          { "action": "setState", "params": { "target": "appData.selectedProduct", "value": "#{item}" } },
          { "action": "navigate", "params": { "route": "/product/#{item.id}" } }
        ],
        "notes": "The selected product state is consumed by product-detail block via inputSymbol"
      },
      {
        "id": "add-to-cart",
        "description": "Add product to cart with upsert, then recompute subtotal + totalItems aggregates, then toast. The two compute steps are NOT optional — without them the cart badge and checkout summary read undefined (v70 cluster T1: 8 fails across 5 templates).",
        "level": "error",
        "block": "product-grid|product-detail|bestsellers-grid",
        "trigger": "add-to-cart|add-to-bag button:tap",
        "steps": [
          {
            "action": "setState",
            "params": {
              "operation": "upsert",
              "target": "appData.cart.items",
              "match": "item => item.id === '#{item.id}'",
              "onNew": { "id": "#{item.id}", "name": "#{item.name}", "price": "#{item.price}", "quantity": 1 },
              "onMatch": { "quantity": "#{item.quantity + 1}" }
            }
          },
          {
            "action": "compute",
            "params": {
              "target": "appData.cart.subtotal",
              "expression": "Math.round(appData.cart.items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100"
            }
          },
          {
            "action": "compute",
            "params": {
              "target": "appData.cart.totalItems",
              "expression": "appData.cart.items.reduce((sum, item) => sum + item.quantity, 0)"
            }
          },
          { "action": "showToast", "params": { "message": "Added to cart", "variant": "success" } }
        ]
      },
      {
        "id": "cart-navigation",
        "description": "Cart icon/button in nav → navigate to cart page",
        "level": "error",
        "block": "store-nav|navbar|header",
        "trigger": "cart icon or cart-wrapper:tap",
        "steps": [
          { "action": "navigate", "params": { "route": "/cart" } }
        ]
      },
      {
        "id": "cart-quantity-update",
        "description": "Increase/decrease item quantity in cart",
        "level": "warning",
        "block": "cart-view",
        "trigger": "qty-plus/qty-minus button:tap",
        "steps_increase": [
          { "action": "setState", "params": { "operation": "updateWhere", "target": "appData.cart.items", "predicate": "item => item.id === '#{item.id}'", "update": { "quantity": "#{item.quantity + 1}" } } }
        ],
        "steps_decrease": [
          { "action": "setState", "params": { "operation": "updateWhere", "target": "appData.cart.items", "predicate": "item => item.id === '#{item.id}'", "update": { "quantity": "#{item.quantity - 1}" } } },
          { "action": "setState", "params": { "operation": "removeWhere", "target": "appData.cart.items", "predicate": "item => item.quantity <= 0" } }
        ]
      },
      {
        "id": "cart-remove-item",
        "description": "Remove item from cart",
        "level": "warning",
        "block": "cart-view",
        "trigger": "remove/delete/trash button:tap",
        "steps": [
          { "action": "setState", "params": { "operation": "removeWhere", "target": "appData.cart.items", "predicate": "item => item.id === '#{item.id}'" } }
        ]
      },
      {
        "id": "checkout-navigation",
        "description": "Proceed to checkout from cart",
        "level": "error",
        "block": "cart-view",
        "trigger": "checkout button:tap",
        "steps": [
          { "action": "navigate", "params": { "route": "/checkout" } }
        ]
      },
      {
        "id": "continue-shopping",
        "description": "Return to home/shop from cart or detail page. Must be unconditional — conditional branches gated by state never seeded by the simulator (e.g. `cart.items.length > 0`) cause `_currentRoute = undefined` failures (v70 cluster T2).",
        "level": "info",
        "block": "cart-view|product-detail",
        "trigger": "back/continue-shopping button:tap",
        "steps": [
          { "action": "navigate", "params": { "route": "/" } }
        ]
      }
    ],
    "requiredState": {
      "appData.cart": { "type": "object", "default": { "items": [] } },
      "appData.selectedProduct": { "type": "object", "default": {} }
    },
    "inlineExpressions": {
      "description": "Dangling computed references that should be inline expressions in UI text",
      "replacements": {
        "appData.cart.total": "appData.cart.items.reduce((s, i) => s + i.price * i.quantity, 0).toFixed(2)",
        "appData.cart.subtotal": "appData.cart.items.reduce((s, i) => s + i.price * i.quantity, 0).toFixed(2)",
        "appData.cart.count": "appData.cart.items.reduce((s, i) => s + i.quantity, 0)",
        "appData.cart.itemCount": "appData.cart.items.length"
      }
    },
    "uiStructureRules": [
      {
        "id": "cart-icon-must-be-link",
        "level": "error",
        "description": "Cart icon in nav must use type='link' with href='/cart' for reliable SPA navigation. Container type requires event injection which is fragile.",
        "block": "store-nav|navbar|header",
        "detect": { "idPattern": "cart", "wrongType": "container" },
        "fix": { "type": "link", "href": "/cart" },
        "reference": "pet-supply-store-new3/blocks/store-nav → cart-link element"
      },
      {
        "id": "product-card-must-have-tap",
        "level": "error",
        "description": "Product card in iterateOver grid must have inline events.tap for card-level click navigation. Without this, only the 'Add to Bag' button is clickable — users expect the entire card to be clickable.",
        "block": "product-grid|bestsellers-grid|product-list",
        "detect": { "insideIterateOver": true, "idPattern": "product-card|item-card", "missingEvents": "tap" },
        "fix": { "events": { "tap": { "flow": "viewProduct" } } },
        "reference": "pet-supply-store-new3/blocks/product-grid → product-card element"
      },
      {
        "id": "checkout-flow-must-clear-cart",
        "level": "error",
        "description": "Checkout/place-order flow must: (1) show success toast, (2) clear cart items, (3) navigate to home or confirmation. Missing cart clear means the cart persists after checkout.",
        "block": "checkout-form",
        "detect": { "flowPattern": "placeOrder|submitOrder|checkout", "missingAction": "setState with target appData.cart" },
        "fix": {
          "appendSteps": [
            { "action": "setState", "params": { "target": "appData.cart.items", "value": [] } },
            { "action": "navigate", "params": { "route": "/" } }
          ]
        },
        "reference": "pet-supply-store-new3/blocks/checkout-form → placeOrder flow"
      },
      {
        "id": "cart-badge-must-use-inline-expression",
        "level": "warning",
        "description": "Cart badge count/total text must use inline expressions (reduce, length) not dangling appData.cart.total references.",
        "block": "store-nav|navbar|header|cart-view",
        "detect": { "textPattern": "#{appData.cart.(total|subtotal|count)}" },
        "reference": "pet-supply-store-new3/blocks/store-nav → cart-count text uses #{appData.cart.items.reduce(...)}"
      }
    ]
  },

  "navigation": {
    "description": "Sidebar/topnav/header navigation flow patterns. Applies to every domain. Surfaced after v70 found 12 broken nav flows (5 empty goHome bodies, 1 callFlow to undefined target, 5 conditional onContinueShopping never firing under simulator, 1 mis-targeted route).",
    "detectWhen": {
      "blockTypes": ["*-sidebar", "*-nav", "*-topnav", "header", "navbar"]
    },
    "requiredFlows": [
      {
        "id": "nav-home-explicit",
        "description": "Sidebar/topnav `goHome` (or `goToHome`/`navigateHome`) MUST contain an explicit `navigate` step. Empty bodies and indirect-only `callFlow` references are the most common cluster T2 defect.",
        "level": "error",
        "block": "*-sidebar|*-nav|*-topnav|header|navbar",
        "trigger": "logo|home|brand-icon:tap",
        "steps": [
          { "action": "navigate", "params": { "route": "/" } }
        ],
        "antipattern": {
          "description": "Two patterns to refuse: (1) empty `steps: []`, (2) sole-step `callFlow` to a target that's not defined anywhere in flow.json or app.json::appFlows.",
          "examples": [
            "{ steps: [] }                                                ← empty",
            "{ steps: [{ action: 'callFlow', params: { flow: 'common_goHome' } }] }  ← unresolved target"
          ]
        }
      },
      {
        "id": "callflow-only-when-defined",
        "description": "Use `callFlow` only when the target exists in (a) the same block's flow.json, (b) another block iterated by the runtime, or (c) `app.json::appFlows`. Prefer inline action steps over single-use callFlow — a typo or stale reference silently no-ops at runtime.",
        "level": "warning",
        "block": "*",
        "validation": "callFlow.params.flow MUST resolve to an existing flow definition; if not resolvable, INLINE the target steps."
      }
    ]
  },

  "search-pagination": {
    "description": "Search input flows that drive a paginated list. v70 cluster T3 found 4 templates where `onSearch` wrote `searchQuery` but never reset `page`/`pageIndex`, so results from page 5 stayed visible after a new query produced 1 page of results.",
    "detectWhen": {
      "blockTypes": ["*-grid", "*-list", "*-table", "*-topnav"],
      "stateKeys": ["searchQuery", "pageIndex", "page", "currentPage"]
    },
    "requiredFlows": [
      {
        "id": "search-with-pagination-reset",
        "description": "When a search input feeds a paginated list, `onSearch` must atomically (1) write the new query AND (2) reset the page index. Order matters: write query first so the page-reset triggers a refetch with the new query.",
        "level": "error",
        "block": "*-grid|*-list|*-table|*-topnav",
        "trigger": "search input:change|input:debounce",
        "steps": [
          { "action": "setState", "params": { "target": "<scope>.searchQuery", "value": "#{event.value}" } },
          { "action": "setState", "params": { "target": "<scope>.pageIndex", "value": 0 } }
        ],
        "notes": "scope is the block-local prefix (e.g. 'team-grid' for team-grid block) OR app-level (e.g. 'appData') depending on whether the data is shared across blocks. Use `pageIndex` (0-indexed) by default; `page` (1-indexed) requires explicit alias declaration in state.json."
      },
      {
        "id": "page-vs-pageIndex-aliasing",
        "description": "`page` and `pageIndex` are interchangeable names but `page` is conventionally 1-indexed and `pageIndex` is conventionally 0-indexed. The narrative-verify validator (post v70 PR) automatically remaps spec-generic `page` ↔ block-actual `pageIndex` (with value adjustment ±1) when both names refer to the same logical pagination cursor. To avoid the remap, pick one convention per app and stick with it.",
        "level": "info",
        "scope": "app-wide-convention"
      }
    ]
  },

  "form": {
    "description": "Form block patterns — validateField, resetForm, saveItem trio + state shape. v70 cluster T4 found 5 form-related setState targets that templates declared but never wrote to.",
    "detectWhen": {
      "blockTypes": ["*-form", "*-detail-sheet", "*-edit-form", "checkout-form", "contact-form"]
    },
    "requiredFlows": [
      {
        "id": "form-validate-field",
        "description": "Per-field validation. MUST write to `appData.form.errors.<field>` (or `<scope>.form.errors.<field>`). Without this write, the error message rendering reads undefined and never appears.",
        "level": "warning",
        "block": "*-form|*-detail-sheet|*-edit-form",
        "trigger": "field input:blur|field input:change",
        "steps": [
          {
            "action": "setState",
            "params": {
              "target": "appData.form.errors.<fieldName>",
              "value": "#{validation_expression}"
            }
          }
        ]
      },
      {
        "id": "form-reset",
        "description": "Reset button clears both values and errors atomically.",
        "level": "warning",
        "block": "*-form|*-detail-sheet|*-edit-form",
        "trigger": "reset|cancel button:tap",
        "steps": [
          { "action": "setState", "params": { "target": "appData.form.values", "value": {} } },
          { "action": "setState", "params": { "target": "appData.form.errors", "value": {} } }
        ]
      },
      {
        "id": "form-save-item",
        "description": "Save/submit fires restApi and writes `_results.saveResult`. The narrative assertion against `_results.saveResult` needs the resultTarget to be exactly `_results.saveResult`.",
        "level": "warning",
        "block": "*-form|*-detail-sheet|*-edit-form",
        "trigger": "save|submit|update button:tap",
        "steps": [
          { "action": "setState", "params": { "target": "appData.form.errors", "value": {} } },
          {
            "action": "restApi",
            "params": {
              "method": "POST",
              "url": "/api/<resource>",
              "body": "#{appData.form.values}",
              "resultTarget": "#_results.saveResult"
            }
          },
          { "action": "showToast", "params": { "message": "Saved", "variant": "success" } }
        ]
      }
    ],
    "requiredState": {
      "appData.form": {
        "type": "object",
        "default": { "values": {}, "errors": {} },
        "description": "Form working state. `values` keyed by field name, `errors` keyed by field name with string error messages (or null when valid)."
      }
    }
  },

  "_antipatterns": {
    "description": "Patterns that LLM generation MUST avoid. v70 narrative-verify catches each as a runtime failure but the LLM should produce code that doesn't trip them in the first place.",
    "rules": [
      {
        "id": "no-double-prefix-paths",
        "level": "error",
        "description": "Never write `appData.appData.cart.items` or `<block>.<block>.foo`. The `appData` prefix appears exactly once. v70 found this in 1 template (product-detail addToCart in bakery-shop).",
        "regex": "appData\\.appData\\.|^([\\w-]+)\\.\\1\\.",
        "fix": "Strip duplicate prefix; the path should start with appData ONCE."
      },
      {
        "id": "no-broken-callflow",
        "level": "error",
        "description": "callFlow.params.flow target must resolve. Common offender: `callFlow common_goHome` when no `common_goHome` is defined anywhere. v70 found 1 (analytics-new3 dash-sidebar). Always inline single-use targets.",
        "fix": "Replace callFlow with the inline action(s) it would have invoked. Example: `[{ action: 'callFlow', params: { flow: 'common_goHome' } }]` → `[{ action: 'navigate', params: { route: '/' } }]`."
      },
      {
        "id": "no-conditional-only-navigate",
        "level": "error",
        "description": "navigate inside a conditional with no fallback fails when the simulator (or runtime) can't satisfy the condition. v70 found 5 (cart-view onContinueShopping gated on `cart.items.length > 0`). Always provide an unconditional fallback navigate.",
        "fix": "Either remove the gate, or add an `else` branch with the fallback navigate."
      },
      {
        "id": "no-missing-cart-aggregates",
        "level": "error",
        "description": "After upsertWhere/removeWhere on `appData.cart.items`, you MUST recompute `appData.cart.subtotal` and `appData.cart.totalItems`. v70 found 8 (5 templates).",
        "fix": "Append the two compute steps from `commerce.add-to-cart.steps[1..2]` after every cart-mutating action."
      },
      {
        "id": "no-toast-only-in-catch",
        "level": "warning",
        "description": "If a flow's only `showToast` step is inside `tryCatch.catch`, the happy-path narrative will not assert it. The narrative generator (post v70 PR #8) skips tryCatch.catch for happy-path narrative. If a success toast is intended, put it in `try` (or outside tryCatch entirely).",
        "fix": "Move success toast to `try`; reserve `catch` for error toasts only."
      },
      {
        "id": "admin-table.root-is-card",
        "level": "error",
        "stage": "editor-meta",
        "description": "v5.8 R1 — an admin-table blockType (`table`, `*-list`, `*-table`, `*-overview`, `*-history*`, `*-access`, `*-plans*`, `*-summary`) must render its ui.root as `type: \"card\"`, or expose a `card` descendant at depth ≤ 2 that owns the toolbar + data-table subtree. Otherwise `.cn-card`'s padding-block contract never applies and the block has no slot surface.",
        "fix": "Wrap the block's UI in `type: \"card\"` with `card-header` + `card-content` (+ `card-footer` optional)."
      },
      {
        "id": "admin-table.required-descendants",
        "level": "error",
        "stage": "editor-meta",
        "description": "v5.8 R2 — admin-table blocks must contain `card-header`, `card-content`, and either `data-table` or `table` in their UI subtree. Missing any of the three breaks the shadcn card scaffold or leaves the table detached from the card shell.",
        "fix": "Add the missing slot(s) under the root card — `card-header` for title/description, `card-content` for the data-table, `card-footer` for pagination."
      },
      {
        "id": "antipattern.nested-box-adhesion",
        "level": "error",
        "stage": "ux",
        "regex": "(?:^|\\s)(?:p|px)-0(?:\\s|$)|(?:^|\\s)(?:p|px)-\\[0(?:px|rem|em)?\\]",
        "description": "v5.8 R3 — `card-content` with utility `p-0`/`px-0` wins over `.cn-card-content`'s component-layer `padding-inline: 24px`; tables then sit flush against the card border (nested-box adhesion). Only acceptable when a descendant opts into `overflow-x-auto`/`overflow-auto` for an intentional edge-to-edge scroll surface.",
        "fix": "Replace `p-0` with `pt-0` (preserves horizontal padding, kills only top padding) as `sales-summary` does. If you genuinely need edge-to-edge, wrap the data-table in a container with `overflow-x-auto`."
      },
      {
        "id": "antipattern.filter-row-not-responsive",
        "level": "error",
        "stage": "ux",
        "description": "v5.8 R4 — filter-row containers with ≥2 form controls (input/select/combobox/date-picker/chip-input) collapse into a single horizontal line on narrow viewports unless they declare `flex-col` + `sm:flex-row`, `flex-wrap` with `min-w-*` on every control, or a responsive grid. Severity is error at ≥3 controls, warning at exactly 2.",
        "fix": "Use `flex flex-col gap-2 sm:flex-row sm:items-center` (preferred). Alternative: `flex flex-wrap` + `min-w-*` on every control, or `grid sm:grid-cols-N`."
      },
      {
        "id": "antipattern.admin-table-responsive-density",
        "level": "warning",
        "stage": "convention",
        "description": "v5.8 R5 — admin-table blocks must carry ≥2 distinct responsive prefixes (`sm:`/`md:`/`lg:`/`xl:`) spread across ≥2 distinct elements (by id). Tightens the coarse Stage 8 `breakpoints` rule that accepted a single `md:p-6` on root — the exact shape 7/8 legacy admin-table offenders ship.",
        "fix": "Distribute responsive classes across the root AND at least one inner element (e.g. card-header, filter row, data-table wrapper)."
      },
      {
        "id": "antipattern.pagination-row-no-wrap",
        "level": "error",
        "stage": "ux",
        "description": "v5.8 R6 — pagination containers (id matches pagination|pager|page-nav, or holds sibling Prev+Next buttons) must allow wrapping on narrow viewports. Plain `flex items-center justify-between` overflows the container on mobile.",
        "fix": "Add `flex-wrap`, or switch to `flex-col` + `sm:flex-row`, or a responsive grid."
      },
      {
        "id": "antipattern.card-without-slot-needs-horizontal-padding",
        "level": "error",
        "stage": "ux",
        "description": "v5.8 R7 — `.cn-card` supplies only `padding-block`; horizontal padding is owned by card-header/card-content/card-footer. A `card` node without any slot child inherits zero horizontal padding and its content hugs the edge.",
        "fix": "Add `p-*` or `px-*` (or an `@container` padding directive) on the card className, or nest the content inside `card-content`."
      }
    ]
  },

  "blog": {
    "description": "Blog/content site patterns — article listing, detail, category filtering",
    "detectWhen": {
      "pageRoutes": ["/article/:id", "/post/:slug"],
      "blockTypes": ["article-grid", "featured-post", "article-detail"],
      "keywords": ["blog", "article", "post", "creator", "content"]
    },
    "requiredFlows": [
      {
        "id": "article-navigation",
        "description": "Article card click → view article detail",
        "level": "error",
        "block": "article-grid|featured-post|article-list",
        "trigger": "article-card container:tap",
        "steps": [
          { "action": "setState", "params": { "target": "appData.selectedArticle", "value": "#{item}" } },
          { "action": "navigate", "params": { "route": "/article/#{item.id}" } }
        ]
      },
      {
        "id": "category-filter",
        "description": "Filter articles by category",
        "level": "warning",
        "block": "article-grid|article-list",
        "trigger": "category-filter select:change or category-tab:tap",
        "steps": [
          { "action": "setState", "params": { "target": "selectedCategory", "value": "#{event.value}" } }
        ]
      }
    ]
  },

  "dashboard": {
    "description": "Analytics/admin dashboard patterns",
    "detectWhen": {
      "blockTypes": ["stats-overview", "chart-panel", "data-table", "metrics-grid"],
      "keywords": ["dashboard", "analytics", "admin", "metrics"]
    },
    "requiredFlows": [
      {
        "id": "date-range-filter",
        "description": "Date range selector changes dashboard data scope",
        "level": "warning",
        "block": "stats-overview|dashboard-header",
        "trigger": "date-range select:change",
        "steps": [
          { "action": "setState", "params": { "target": "dateRange", "value": "#{event.value}" } }
        ]
      },
      {
        "id": "row-detail-navigation",
        "description": "Table row click → detail view",
        "level": "info",
        "block": "data-table|orders-table",
        "trigger": "table-row:tap",
        "steps": [
          { "action": "setState", "params": { "target": "appData.selectedItem", "value": "#{item}" } }
        ]
      }
    ]
  },

  "portfolio": {
    "description": "Portfolio/showcase patterns",
    "detectWhen": {
      "blockTypes": ["project-grid", "portfolio-showcase", "case-study"],
      "keywords": ["portfolio", "showcase", "project", "case-study"]
    },
    "requiredFlows": [
      {
        "id": "project-detail-navigation",
        "description": "Project card click → view project detail",
        "level": "warning",
        "block": "project-grid|portfolio-showcase",
        "trigger": "project-card:tap",
        "steps": [
          { "action": "setState", "params": { "target": "appData.selectedProject", "value": "#{item}" } }
        ]
      },
      {
        "id": "contact-form-submit",
        "description": "Contact form submission",
        "level": "warning",
        "block": "contact-form",
        "trigger": "submit button:tap",
        "steps": [
          { "action": "setState", "params": { "target": "_loading", "value": true } },
          { "action": "showToast", "params": { "message": "Message sent!", "variant": "success" } },
          { "action": "setState", "params": { "target": "_loading", "value": false } }
        ]
      }
    ]
  }
}
